import { withOneTrust } from '@segment/analytics-consent-wrapper-onetrust';
import { AnalyticsBrowser, type EventProperties, type Options } from '@segment/analytics-next';
import { ONETRUST_CATEGORIES } from 'libs/oneTrust';
import { hasConsentFromBrowser } from 'libs/oneTrust/clientConsent';
import { GTM_DESTINATION_EVENTS, isGtmDirectRoutingEnabled, pushGtmEvent } from './gtm';

const segment = new AnalyticsBrowser();

if (typeof window !== 'undefined') {
  const writeKey = window.__CHIME_CONFIG__?.segmentWriteKey;
  /**
   * Segment is categorized Performance (C0002); a sell/share opt-out (or GPC)
   * revokes C0002, so we skip the load for opted-out users. Defaults to opt-in when
   * there's no signal, so new and consented visitors still get Segment.
   *
   * When the chime_com_gtm_direct_routing gate is off, GTM is injected via Segment
   * so we must not load Segment to block GTM for opted-out users.
   */
  if (writeKey && hasConsentFromBrowser(ONETRUST_CATEGORIES.PERFORMANCE)) {
    // Use Fetch `keepalive` so events fired immediately before navigation still reach Segment.
    // https://www.twilio.com/docs/segment/connections/sources/catalog/libraries/website/javascript#keepalive
    withOneTrust(segment).load(
      { writeKey },
      {
        integrations: {
          'Segment.io': {
            deliveryStrategy: {
              strategy: 'batching',
              config: {
                keepalive: true,
              },
            },
          },
          ...(isGtmDirectRoutingEnabled() ? { 'Google Tag Manager': false } : {}),
        },
      },
    );
  }
}

/**
 * Analytics facade.
 *
 * `page` and most `track` calls go straight to Segment, unchanged. When the
 * chime_com_gtm_direct_routing gate is on, GTM_DESTINATION_EVENTS allowlist events are
 * ALSO pushed into the GTM dataLayer directly and sent to Segment with the
 * GTM destination disabled — so GTM fires each one once (via the dataLayer)
 * while other Segment destinations still receive it. When the gate is off,
 * allowlist events flow to Segment unchanged and Segment's GTM destination
 * delivers them to GTM (current production behavior).
 *
 * The push is intentionally NOT consent-gated: the dataLayer is a passive queue,
 * and Targeting consent is enforced at the loader (components/googleTagManager.tsx
 * only boots GTM-N3Z9ZNR once C0004 is granted). Events pushed before consent sit
 * unprocessed until GTM loads — the same buffer-then-deliver behavior Segment's
 * withOneTrust wrapper gives the GTM destination today.
 */
export const analytics = {
  page: segment.page.bind(segment),
  track: (eventName: string, properties: EventProperties = {}, options?: Options, callback?: () => void) => {
    if (isGtmDirectRoutingEnabled() && GTM_DESTINATION_EVENTS.has(eventName)) {
      pushGtmEvent(eventName, properties);

      return segment.track(
        eventName,
        properties,
        { ...options, integrations: { ...options?.integrations, 'Google Tag Manager': false } },
        callback,
      );
    }

    return segment.track(eventName, properties, options, callback);
  },
};
